import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Drawer (Sheet)
 *
 * A slide-out panel for detail views, forms, or contextual content.
 * Animations are always on and respect prefers-reduced-motion.
 */

export type DrawerSide = 'left' | 'right'

export interface DrawerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	side?: DrawerSide
	width?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
	surface?: 'navy' | 'cream'
	closeOnBackdropClick?: boolean
	closeOnEscape?: boolean
	children?: React.ReactNode
	className?: string
}

const widthStyles: Record<string, string> = {
	sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl', full: 'max-w-full',
}

const surfaceStyles: Record<string, string> = {
	navy: 'bg-navy-900 text-cream-50 border-navy-700',
	cream: 'bg-card text-card-foreground border-border',
}

const slideVariants = {
	left: { initial: { x: '-100%' }, animate: { x: 0 }, exit: { x: '-100%' } },
	right: { initial: { x: '100%' }, animate: { x: 0 }, exit: { x: '100%' } },
}

export function Drawer({
	open,
	onOpenChange,
	side = 'right',
	width = 'md',
	surface = 'cream',
	closeOnBackdropClick = true,
	closeOnEscape = true,
	children,
	className,
}: DrawerProps) {
	const panelRef = React.useRef<HTMLDivElement>(null)
	const previousFocusRef = React.useRef<HTMLElement | null>(null)

	React.useEffect(() => {
		if (!open) return
		previousFocusRef.current = document.activeElement as HTMLElement
		document.body.style.overflow = 'hidden'
		const timer = setTimeout(() => panelRef.current?.focus(), 50)
		return () => {
			clearTimeout(timer)
			document.body.style.overflow = ''
			previousFocusRef.current?.focus()
		}
	}, [open])

	React.useEffect(() => {
		if (!open || !closeOnEscape) return
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Escape') { e.stopPropagation(); onOpenChange(false) }
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [open, closeOnEscape, onOpenChange])

	const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
		if (e.key !== 'Tab' || !panelRef.current) return
		const focusable = panelRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
		const first = focusable[0]
		const last = focusable[focusable.length - 1]
		if (e.shiftKey) {
			if (document.activeElement === first) { e.preventDefault(); last?.focus() }
		} else {
			if (document.activeElement === last) { e.preventDefault(); first?.focus() }
		}
	}, [])

	const slide = slideVariants[side]

	return (
		<AnimatePresence>
			{open && (
				<div className="fixed inset-0 z-overlay">
					<motion.div
						className="absolute inset-0 bg-navy/30"
						onClick={closeOnBackdropClick ? () => onOpenChange(false) : undefined}
						aria-hidden="true"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
					/>
					<motion.div
						ref={panelRef}
						role="dialog"
						aria-modal="true"
						tabIndex={-1}
						onKeyDown={handleKeyDown}
						className={cn(
							'absolute top-0 bottom-0 flex flex-col border-l shadow-xl',
							side === 'right' ? 'right-0' : 'left-0 border-l-0 border-r',
							widthStyles[width],
							surfaceStyles[surface],
							className,
						)}
						initial={slide.initial}
						animate={slide.animate}
						exit={slide.exit}
						transition={{ type: 'spring', stiffness: 300, damping: 30 }}
					>
						{children}
					</motion.div>
				</div>
			)}
		</AnimatePresence>
	)
}

export function DrawerClose({ onClick, className }: { onClick?: () => void; className?: string }) {
	return (
		<button onClick={onClick} className={cn('absolute top-4 right-4 p-1.5 rounded-md transition-colors duration-fast hover:bg-navy-100 text-muted-foreground hover:text-foreground', className)} aria-label="Close">
			<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
				<path d="M18 6 6 18" />
				<path d="m6 6 12 12" />
			</svg>
		</button>
	)
}

export function DrawerHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn('relative flex-shrink-0 p-6 pb-4 border-b border-border', className)} {...props} />
}
export function DrawerTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
	return <h2 className={cn('font-display text-base uppercase tracking-trajan-wide font-bold pr-8', className)} {...props} />
}
export function DrawerDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
	return <p className={cn('text-sm text-muted-foreground font-ui mt-1', className)} {...props} />
}
export function DrawerBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn('flex-1 overflow-y-auto p-6 scrollbar-warm', className)} {...props}>{props.children}</div>
}
export function DrawerFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn('flex-shrink-0 p-6 pt-4 border-t border-border', className)} {...props}>{props.children}</div>
}
